import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { HttpConnectorError } from "../models/http-connector-error.model";
import { Listener } from '../models/listener.model';
import { RestEndpoint } from '../models/rest-endpoint.model';
import { UserLoginDto } from '../netgen/UserLoginDto';
import { AuthLoginResult } from '../shared/AuthLoginResult';
import { LoginUser } from '../shared/LoginUser';

@Injectable({providedIn: "root"})
export class AuthenticationService {

    router = inject(Router);

    anonymousOnly = false;
    userDetails: LoginUser = null;

    onUserChanged = new Listener<void>();

    private accessToken: string = null;
    private fetchAuthenticationHeadersInstalled = false;
    private httpOnlyAuthenticationCookieAvailable = false;

    constructor() {
        GuiUtils.checkSingleton(this);
    }

    getToken(): string {
        if (this.accessToken == null) {
            let accessToken = localStorage.getItem('accessToken');
            if (!Utils.isNoe(accessToken)) {
                this.accessToken = accessToken;
            }
        }
        return this.accessToken;
    }

    getUserName(): string {
        return this.userDetails?.userName;
    }

    getUserId(): number {
        return this.userDetails?.getDatabaseUser().id;
    }

    usesHttpOnlyAuthenticationCookie(): boolean {
        return this.httpOnlyAuthenticationCookieAvailable;
    }

    setHttpOnlyAuthenticationCookieAvailable(value: boolean): void {
        this.httpOnlyAuthenticationCookieAvailable = value;
        if (value) {
            this.installFetchAuthenticationHeaders();
        }
    }

    authenticationCookieWasSet(): void {
        this.setToken(null);
    }

    private installFetchAuthenticationHeaders(): void {
        if (this.fetchAuthenticationHeadersInstalled) {
            return;
        }

        this.fetchAuthenticationHeadersInstalled = true;
        const originalFetch = window.fetch.bind(window);
        window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const requestUrl = new URL(input instanceof Request ? input.url : input.toString(), window.location.href);
            const requestMethod = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
            if (this.usesHttpOnlyAuthenticationCookie() && requestUrl.origin == window.location.origin &&
                !["GET", "HEAD", "OPTIONS", "TRACE"].includes(requestMethod)) {
                const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : []));
                headers.set("portal-authentication-mode", "cookie");
                headers.set("portal-csrf", "1");
                init = {...init, headers};
            }
            return originalFetch(input, init);
        };
    }

    async clearLogin(returnUrl = true): Promise<void> {
        if (this.anonymousOnly) {
            return;
        }

        this.setToken(null);
        this.userDetails = null;
        let url = Strings.removePrefix(window.location.href, window.location.origin);
        if (url.startsWith("/base/")) {
            return;
        }
        if (url.length <= 1) {
            returnUrl = false;
        }
        await this.router.navigateByUrl("/base/login" + (returnUrl ? "?returnUrl=" + Strings.removePrefix(url, "/base/login?returnUrl=") : ""));
    }

    isLoggedIn(): boolean {
        return this.anonymousOnly || this.userDetails != null;
    }

    setToken(accessToken: string): void {
        this.accessToken = accessToken;
        localStorage.setItem('accessToken', accessToken || "");
    }

    async login(data: UserLoginDto): Promise<LoginUser> {
        try {
            let res = await RestEndpoint.main().body(data).post().run("api/app/auth/login").get(LoginUser);
            if (res.loginResult == AuthLoginResult.ok && (this.usesHttpOnlyAuthenticationCookie() || !Utils.isNoe(res.accessToken))) {
                if (!this.usesHttpOnlyAuthenticationCookie()) {
                    this.setToken(res.accessToken);
                }
                if (!await this.updateUserDetails()) {
                    this.setToken(null);
                    return Objects.fromPlain(LoginUser, {
                        userName: res.userName,
                        loginResult: AuthLoginResult.unspecifiedError
                    });
                }
                await this.onUserChanged.emit(null);
            }
            return res;
        }
        catch (error) {
            console.log("Fehler beim Login: " + Objects.toJson(error));
            if (error instanceof HttpConnectorError && error.response?.status == 401) {
                return Objects.fromPlain(LoginUser, {
                    loginResult: AuthLoginResult.invalidPassword
                });
            }
            return Objects.fromPlain(LoginUser, {
                loginResult: AuthLoginResult.unspecifiedError
            });
        }
    }

    async logout(): Promise<void> {
        try {
            await RestEndpoint.main().post().run("api/app/auth/logout").getText();
        }
        finally {
            await this.clearLogin(false);
        }
    }

    async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
        let result = await RestEndpoint.main().post().body({oldPassword, newPassword}).run("api/app/auth/changePassword").getBool();
        if (result) {
            await this.clearLogin(false);
        }
        return result;
    }

    async updateUserDetails(): Promise<boolean> {
        if (this.anonymousOnly) {
            this.userDetails = Objects.fromPlain(LoginUser, {
                userName: "Anonymous",
                accessToken: ""
            });
            return true;
        }
        else {
            try {
                let s = await RestEndpoint.main().run("api/app/auth/getuserdetails").getText();
                if (Utils.isNoe(s)) {
                    await this.clearLogin();
                    return false;
                }
                else {
                    this.userDetails = Objects.fromPlainUnsafe(LoginUser, Objects.fromJson(s));
                    if (Strings.is(this.userDetails)) {
                        throw new Error("userDetails ist vom Typ string: " + s);
                    }
                    return true;
                }
            }
            catch (error) {
                //Verbindungsproblem - Nicht den Login löschen
                if (error instanceof HttpConnectorError && error.response?.status == 0) {
                    return false;
                }
                await this.clearLogin();
                return false;
            }
        }
    }
}
